/**
 * Product Gallery JavaScript
 * Handles mobile carousel, desktop grid, and zoom modal
 */

class ProductGallery {
  constructor(container) {
    this.container = container;
    this.carousel = container.querySelector('[data-gallery-carousel]');
    this.dots = container.querySelector('[data-gallery-dots]');
    this.images = container.querySelectorAll('[data-gallery-image]');
    this.mediaData = null;
    this.currentIndex = 0;
    this.zoomLevel = 1; // 1, 1.5, 3
    this.isZoomed = false;
    this.pinchStartDistance = 0;
    this.pinchStartZoom = 1;
    this.isVideoInModal = false;
    
    // Map original media indices to image-only indices (legacy, for image-only flows)
    this.imageIndexMap = this.buildImageIndexMap();
    
    this.init();
  }
  
  buildImageIndexMap() {
    // Build a map from original media index to image-only index
    // This allows zoom modal to skip videos
    const map = {};
    let imageIndex = 0;
    
    const allItems = this.container.querySelectorAll('[data-media-type]');
    allItems.forEach((item, originalIndex) => {
      const mediaType = item.dataset.mediaType;
      if (mediaType === 'image') {
        map[originalIndex] = imageIndex;
        imageIndex++;
      }
    });
    
    return map;
  }
  
  init() {
    if (!this.container) return;

    console.log('[ProductGallery] Initializing gallery', {
      container: this.container,
      imageCount: this.images.length
    });

    // Load media data - try multiple lookup methods
    let sectionId = this.container.closest('[data-section-id]')?.dataset.sectionId;
    if (!sectionId) {
      const section = this.container.closest('[id^="shopify-section-"]');
      if (section) {
        sectionId = section.id.replace('shopify-section-', '');
      }
    }
    let mediaDataScript = sectionId ? document.getElementById('product-media-data-' + sectionId) : null;
    if (!mediaDataScript) {
      // Fallback: find any product-media-data script (e.g. after Section Rendering API)
      mediaDataScript = document.querySelector('script[id^="product-media-data-"]');
      if (mediaDataScript) {
        sectionId = mediaDataScript.id.replace('product-media-data-', '');
      }
    }
    if (mediaDataScript) {
      try {
        this.mediaData = JSON.parse(mediaDataScript.textContent);
      } catch (e) {
        console.error('Failed to parse media data:', e);
      }
    }
    
    // Mobile carousel - only initialize on mobile
    if (this.carousel && window.innerWidth < 768) {
      this.initCarousel();

      // Preload next images immediately on page load (don't wait for user interaction)
      // Use requestIdleCallback if available, otherwise setTimeout
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.preloadAdjacentImages();
        }, { timeout: 1000 });
      } else {
        setTimeout(() => {
          this.preloadAdjacentImages();
        }, 100);
      }
    }
    
    // Image click handlers for zoom (desktop and mobile)
    this.images.forEach((image) => {
      image.addEventListener('click', (e) => {
        const img = image.querySelector('img');
        const mediaIndex = parseInt(image.dataset.mediaIndex ?? image.dataset.index ?? '0', 10);
        this.openZoomModal(mediaIndex, img || image, { isVideo: false });
      });
    });

    // Mobile: Video tap opens zoom modal (video wrapper is tappable)
    this.container.querySelectorAll('[data-video-mobile]').forEach((wrapper) => {
      wrapper.addEventListener('click', (e) => {
        if (window.innerWidth >= 768) return; // Desktop uses zoom button on video
        e.stopPropagation();
        const slide = wrapper.closest('[data-slide-index]');
        const mediaIndex = slide ? parseInt(slide.dataset.slideIndex ?? '0', 10) : 0;
        this.openZoomModal(mediaIndex, wrapper, { isVideo: true });
      });
    });
    
    // Mobile: Zoom button handler - always opens to current carousel position
    const zoomButton = this.container.querySelector('[data-gallery-zoom]');
    if (zoomButton) {
      // Show/hide zoom button based on current slide type
      this.updateZoomButtonVisibility();
      
      // Update zoom button visibility when carousel scrolls
      if (this.carousel) {
        this.carousel.addEventListener('scroll', () => {
          this.updateZoomButtonVisibility();
        }, { passive: true });
      }
      
      zoomButton.addEventListener('click', (e) => {
        e.stopPropagation();
        // Sync index from scroll position before opening (handles swipe-then-immediate-tap)
        const carouselIndex = this.getCarouselIndex();
        this.currentIndex = carouselIndex;
        const slides = this.carousel.querySelectorAll('[data-slide-index]');
        const currentSlide = slides[carouselIndex];
        const sourceImage = currentSlide ? currentSlide.querySelector('img') : null;
        let videoWrapper = currentSlide ? currentSlide.querySelector('[data-video-mobile]') : null;
        // Fallback: if slide has video but no data-video-mobile wrapper, use slide as source
        if (!videoWrapper && currentSlide?.querySelector('video')) {
          videoWrapper = currentSlide;
        }
        const isVideoByMediaData = this.mediaData?.media?.[carouselIndex]?.media_type === 'video';
        
        // Prefer mediaData for type - getCarouselIndex can be off on peek carousel
        if (isVideoByMediaData && (videoWrapper || currentSlide)) {
          this.openZoomModal(carouselIndex, videoWrapper || currentSlide, { isVideo: true });
        } else if (sourceImage) {
          this.openZoomModal(carouselIndex, sourceImage, { isVideo: false });
        } else if (videoWrapper) {
          this.openZoomModal(carouselIndex, videoWrapper, { isVideo: true });
        }
      });
    }

    // Mobile: Play/pause button - toggles current slide video
    const playPauseMobile = this.container.querySelector('[data-gallery-play-pause-mobile]');
    if (playPauseMobile && this.carousel) {
      playPauseMobile.addEventListener('click', (e) => {
        e.stopPropagation();
        const carouselIndex = this.getCarouselIndex();
        const slides = this.carousel.querySelectorAll('[data-slide-index]');
        const currentSlide = slides[carouselIndex];
        const video = currentSlide ? currentSlide.querySelector('video') : null;
        if (!video) return;
        if (video.paused) {
          video.play().catch(() => {});
          playPauseMobile.classList.add('is-playing');
          playPauseMobile.setAttribute('aria-label', 'Pause video');
        } else {
          video.pause();
          playPauseMobile.classList.remove('is-playing');
          playPauseMobile.setAttribute('aria-label', 'Play video');
        }
      });
    }

    // Desktop: Zoom button on videos - opens video in zoom modal
    this.container.querySelectorAll('[data-gallery-zoom-desktop]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mediaIndex = parseInt(btn.dataset.mediaIndex, 10);
        const videoWrapper = btn.closest('[data-video-desktop]');
        const sourceElement = videoWrapper || btn;
        this.openZoomModal(mediaIndex, sourceElement, { isVideo: true });
      });
    });

    // Desktop: Play/pause buttons on each video
    this.container.querySelectorAll('[data-gallery-play-pause]:not([data-gallery-play-pause-mobile])').forEach((btn) => {
      const wrapper = btn.closest('[data-video-desktop]');
      const video = wrapper ? wrapper.querySelector('video') : null;
      if (!video) return;
      const syncButtonState = () => {
        btn.classList.toggle('is-playing', !video.paused);
        btn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      };
      video.addEventListener('play', syncButtonState);
      video.addEventListener('pause', syncButtonState);
      syncButtonState();
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
    
    // Initialize video handling
    this.initVideos();
  }
  
  getCarouselIndex() {
    if (!this.carousel) return 0;
    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    const firstSlide = slides[0];
    if (!firstSlide) return 0;
    const slideWidth = firstSlide.offsetWidth;
    const gapStr = getComputedStyle(this.carousel).gap;
    const gap = gapStr ? parseFloat(gapStr) || 1 : 1;
    const step = slideWidth + gap;
    const carouselWidth = this.carousel.clientWidth;
    const scrollLeft = this.carousel.scrollLeft;
    const maxScroll = this.carousel.scrollWidth - carouselWidth;

    if (slides.length <= 1) return 0;
    if (scrollLeft <= 0) return 0;
    if (maxScroll <= 0) return 0;
    if (scrollLeft >= maxScroll - 1) return slides.length - 1;

    // Match updateDots formula for peek carousel consistency
    const fractional = scrollLeft / step;
    const index = Math.round(Math.max(0, Math.min(slides.length - 1, fractional)));
    return index;
  }
  
  initCarousel() {
    if (!this.carousel) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scroll snap behavior - native CSS handles the swipe, we just update dots
    let scrollRaf = 0;
    let scrollDebounceId = 0;
    const scrollHandler = () => {
      if (prefersReducedMotion) {
        if (scrollDebounceId) clearTimeout(scrollDebounceId);
        scrollDebounceId = setTimeout(() => {
          this.updateDots();
          this.preloadAdjacentImages();
          scrollDebounceId = 0;
        }, 100);
      } else {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(() => {
          this.updateDots();
          this.preloadAdjacentImages();
          scrollRaf = 0;
        });
      }
    };
    this.carousel.addEventListener('scroll', scrollHandler);
    
    // Dot click handlers
    if (this.dots) {
      const dotButtons = this.dots.querySelectorAll('[data-dot-index]');
      dotButtons.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(index);
          // Preload after navigation
          setTimeout(() => this.preloadAdjacentImages(), 100);
        });
      });
    }
    
    // Initial preload of next images immediately
    this.preloadAdjacentImages();
    
    // Use Intersection Observer for more efficient preloading
    this.initCarouselPreloading();
    
    // Also preload on touch/pointer events (user is about to swipe)
    this.carousel.addEventListener('touchstart', () => {
      this.preloadAdjacentImages();
    }, { passive: true });
    
    this.carousel.addEventListener('pointerdown', () => {
      this.preloadAdjacentImages();
    }, { passive: true });
    
    this.updateDots();
  }
  
  initCarouselPreloading() {
    if (!this.carousel) return;
    
    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    if (slides.length === 0) return;
    
    // Create Intersection Observer to detect when slides are approaching viewport
    // Use a larger rootMargin to start preloading earlier
    const observerOptions = {
      root: this.carousel,
      rootMargin: '100% 0%', // Start preloading when slide is 100% away (one full slide)
      threshold: [0, 0.1, 0.5, 1]
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          const slideIndex = parseInt(entry.target.dataset.slideIndex);
          // Preload next and previous images immediately
          this.preloadImageAtIndex(slideIndex + 1); // Preload next
          this.preloadImageAtIndex(slideIndex - 1); // Preload previous
        }
      });
    }, observerOptions);
    
    // Observe all slides
    slides.forEach(slide => observer.observe(slide));
    
    // Store observer for cleanup if needed
    this.carouselObserver = observer;
    
    // Also preload immediately based on current position
    const currentIndex = this.getCarouselIndex();
    // Preload next 2 images for smoother swiping
    this.preloadImageAtIndex(currentIndex + 1);
    this.preloadImageAtIndex(currentIndex + 2);
    this.preloadImageAtIndex(currentIndex - 1);
  }
  
  preloadAdjacentImages() {
    if (!this.carousel) return;
    
    const currentIndex = this.getCarouselIndex();
    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    
    // Preload next 2 images for smoother swiping
    if (currentIndex < slides.length - 1) {
      this.preloadImageAtIndex(currentIndex + 1);
    }
    if (currentIndex < slides.length - 2) {
      this.preloadImageAtIndex(currentIndex + 2);
    }
    
    // Preload previous image (helps with back navigation)
    if (currentIndex > 0) {
      this.preloadImageAtIndex(currentIndex - 1);
    }
  }
  
  preloadImageAtIndex(index) {
    if (!this.carousel) return;
    
    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    if (index < 0 || index >= slides.length) return;
    
    const slide = slides[index];
    const img = slide.querySelector('img');
    if (!img) return;
    
    // Check if already loaded or preloaded
    if (img.complete && img.naturalWidth > 0) {
      return; // Already loaded
    }
    if (img.dataset.preloaded === 'true') {
      return; // Already being preloaded
    }
    
    // Mark as preloading immediately to prevent duplicate work
    img.dataset.preloaded = 'true';
    
    // Get the best image source for preloading
    // For mobile, use 750w which matches the src attribute
    let preloadSrc = img.src;
    
    // Try to get the best size from srcset
    if (img.srcset) {
      const srcsetEntries = img.srcset.split(',').map(entry => {
        const parts = entry.trim().split(/\s+/);
        return {
          url: parts[0],
          width: parts[1] ? parseInt(parts[1]) : 0
        };
      });
      
      // Find the best size for mobile (750w or closest larger)
      const mobileSize = 750;
      const bestEntry = srcsetEntries
        .filter(e => e.width >= mobileSize)
        .sort((a, b) => a.width - b.width)[0] || 
        srcsetEntries.sort((a, b) => b.width - a.width)[0];
      
      if (bestEntry && bestEntry.url) {
        preloadSrc = bestEntry.url;
      }
    }
    
    // CRITICAL: Force the image to load by:
    // 1. Removing lazy loading attribute
    // 2. Setting src directly (bypasses lazy loading completely)
    // 3. Creating Image object to actually fetch it
    
    // Remove lazy loading - this is essential
    if (img.loading === 'lazy') {
      img.removeAttribute('loading');
      img.loading = 'eager';
    }
    
    // Store original attributes
    const originalSrcset = img.srcset || '';
    
    // Force immediate load by setting src directly
    // This bypasses the browser's lazy loading mechanism
    img.src = preloadSrc;
    
    // Create Image object to actually fetch the image (forces network request)
    const preloadImage = new Image();
    preloadImage.src = preloadSrc;
    
    // Restore srcset after image starts loading (for responsive behavior)
    // But keep the src set so it loads immediately
    if (originalSrcset) {
      // Wait a frame to let the src load start
      requestAnimationFrame(() => {
        img.srcset = originalSrcset;
        // Ensure src is still set as fallback
        if (!img.src || img.src === '') {
          img.src = preloadSrc;
        }
      });
    }
    
    // Also add link preload for browser hint
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = preloadSrc;
    link.fetchPriority = 'high';
    
    // Check if already exists
    const existingLink = document.querySelector(`link[rel="preload"][as="image"][href="${preloadSrc}"]`);
    if (!existingLink) {
      document.head.appendChild(link);
    }
  }
  
  updateZoomButtonVisibility() {
    const zoomButton = this.container.querySelector('[data-gallery-zoom]');
    const playPauseMobile = this.container.querySelector('[data-gallery-play-pause-mobile]');
    if (!this.carousel) return;
    
    const carouselIndex = this.getCarouselIndex();
    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    const currentSlide = slides[carouselIndex];
    
    // Check if current slide has an image or video
    if (currentSlide) {
      const hasVideo = currentSlide.querySelector('[data-video-mobile]') !== null;
      
      // Zoom button always visible on all slides
      if (zoomButton) {
        zoomButton.classList.add('visible');
        zoomButton.style.pointerEvents = 'auto';
      }
      
      // Play/pause only on video slides
      if (playPauseMobile) {
        if (hasVideo) {
          playPauseMobile.classList.add('visible');
          playPauseMobile.style.pointerEvents = 'auto';
          const video = currentSlide.querySelector('video');
          playPauseMobile.classList.toggle('is-playing', video && !video.paused);
          playPauseMobile.setAttribute('aria-label', video && !video.paused ? 'Pause video' : 'Play video');
        } else {
          playPauseMobile.classList.remove('visible');
          playPauseMobile.style.pointerEvents = 'none';
        }
      }
    }
  }
  
  updateDots() {
    if (!this.carousel || !this.dots) return;

    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    const firstSlide = slides[0];
    const slideWidth = firstSlide ? firstSlide.offsetWidth : this.carousel.offsetWidth;
    const gapStr = getComputedStyle(this.carousel).gap;
    const gap = gapStr ? parseFloat(gapStr) || 1 : 1;
    const step = slideWidth + gap;
    const carouselWidth = this.carousel.clientWidth;
    const scrollLeft = this.carousel.scrollLeft;
    const maxScroll = this.carousel.scrollWidth - carouselWidth;

    let currentIndex;
    let indicatorPosition;
    if (slides.length <= 1) {
      currentIndex = 0;
      indicatorPosition = 0;
    } else if (maxScroll <= 0) {
      currentIndex = 0;
      indicatorPosition = 0;
    } else {
      const fractional = scrollLeft / step;
      currentIndex = Math.round(Math.max(0, Math.min(slides.length - 1, fractional)));
      indicatorPosition = Math.max(0, Math.min(slides.length - 1, fractional));
      if (currentIndex === slides.length - 1) {
        indicatorPosition = slides.length - 1;
      }
    }
    
    const dots = this.dots.querySelectorAll('[data-dot-index]');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === currentIndex);
    });
    this.dots.style.setProperty('--indicator-position', String(indicatorPosition));
    
    this.currentIndex = currentIndex;
    
    // Update zoom button visibility when dots change
    this.updateZoomButtonVisibility();
  }
  
  goToSlide(index) {
    if (!this.carousel) return;

    const slides = this.carousel.querySelectorAll('[data-slide-index]');
    if (!slides[index]) return;

    const firstSlide = slides[0];
    const slideWidth = firstSlide ? firstSlide.offsetWidth : 0;
    const gapStr = getComputedStyle(this.carousel).gap;
    const gap = gapStr ? parseFloat(gapStr) || 1 : 1;
    const step = slideWidth + gap;
    const carouselWidth = this.carousel.clientWidth;
    const maxScroll = this.carousel.scrollWidth - carouselWidth;

    let targetScroll;
    if (index === 0) {
      targetScroll = 0;
    } else if (index === slides.length - 1) {
      targetScroll = maxScroll;
    } else {
      targetScroll = step * index + slideWidth / 2 - carouselWidth / 2;
      targetScroll = Math.max(0, Math.min(maxScroll, targetScroll));
    }

    this.carousel.scrollTo({ left: targetScroll, behavior: 'smooth' });
    this.currentIndex = index;
  }
  
  openZoomModal(mediaIndex, sourceElement = null, options = {}) {
    const isVideo = options.isVideo === true;
    
    this.currentIndex = mediaIndex;
    this.currentMediaIndex = mediaIndex;
    this.zoomLevel = 1;
    this.isZoomed = false;
    this.isVideoInModal = isVideo;
    
    let modal = document.getElementById('product-gallery-modal');
    if (!modal) {
      modal = this.createZoomModal();
      document.body.appendChild(modal);
    }
    
    const image = modal.querySelector('.product-gallery-modal__image');
    const videoEl = modal.querySelector('.product-gallery-modal__video');
    const imageContainer = modal.querySelector('.product-gallery-modal__image-container');
    
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    const totalMedia = this.mediaData?.media?.length ?? this.container.querySelectorAll('[data-slide-index]').length;
    if (totalMedia <= 1) {
      modal.classList.add('product-gallery-modal--single-image');
    } else {
      modal.classList.remove('product-gallery-modal--single-image');
    }
    
    if (isVideo) {
      modal.classList.add('product-gallery-modal--video');
      this.updateModalVideo(mediaIndex, sourceElement, image, videoEl, imageContainer);
    } else {
      modal.classList.remove('product-gallery-modal--video');
      this.updateModalImage(mediaIndex, sourceElement ? () => {
        this.animateZoomIn(image, imageContainer, sourceElement);
      } : null);
    }
    
    const firstFocusable = modal.querySelector('button');
    if (firstFocusable) firstFocusable.focus();
  }
  
  animateZoomIn(modalImage, imageContainer, sourceElement) {
    // Get source element position and dimensions
    const sourceRect = sourceElement.getBoundingClientRect();
    const containerRect = imageContainer.getBoundingClientRect();
    
    // Calculate initial scale (source size / final size)
    const intrinsicW = modalImage.naturalWidth || modalImage.videoWidth || containerRect.width;
    const intrinsicH = modalImage.naturalHeight || modalImage.videoHeight || containerRect.height;
    const finalWidth = Math.min(containerRect.width, intrinsicW);
    const finalHeight = Math.min(containerRect.height, intrinsicH);
    
    const scaleX = sourceRect.width / finalWidth;
    const scaleY = sourceRect.height / finalHeight;
    const initialScale = Math.min(scaleX, scaleY, 1); // Don't scale up if source is larger
    
    // Calculate initial position (center of source relative to container center)
    const sourceCenterX = sourceRect.left + sourceRect.width / 2;
    const sourceCenterY = sourceRect.top + sourceRect.height / 2;
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const containerCenterY = containerRect.top + containerRect.height / 2;
    
    const initialX = sourceCenterX - containerCenterX;
    const initialY = sourceCenterY - containerCenterY;
    
    // Set initial transform
    modalImage.style.transform = `translate(${initialX}px, ${initialY}px) scale(${initialScale})`;
    modalImage.style.opacity = '1';
    
    // Trigger animation to final state
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modalImage.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s';
        modalImage.style.transform = 'translate(0, 0) scale(1)';
        
        // Reset transition after animation completes
        setTimeout(() => {
          modalImage.style.transition = '';
        }, 400);
      });
    });
  }
  
  createZoomModal() {
    const modal = document.createElement('div');
    modal.id = 'product-gallery-modal';
    modal.className = 'product-gallery-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product image zoom');
    
    modal.innerHTML = `
      <div class="product-gallery-modal__container">
        <div class="product-gallery-modal__image-container">
          <div class="product-gallery-modal__loading-spinner" aria-hidden="true">
            <svg class="icon icon-spinner" viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="#666">
              <use xlink:href="#icon-spinner" />
            </svg>
          </div>
          <img class="product-gallery-modal__image zoom-1x" alt="" />
          <video class="product-gallery-modal__video" muted loop playsinline preload="auto" style="display: none;"></video>
        </div>
        <div class="product-gallery-modal__tap-hint is-loading" aria-hidden="true">TAP AND DRAG TO ZOOM</div>
        <div class="product-gallery-modal__controls">
          <button class="product-gallery-modal__video-play-pause" aria-label="Pause video" style="display: none;">
            <span class="product-gallery-modal__video-play-icon" aria-hidden="true"><svg class="icon icon-play" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="24" height="24"><use href="#icon-play" /></svg></span>
            <span class="product-gallery-modal__video-pause-icon" aria-hidden="true"><svg class="icon icon-pause" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="24" height="24"><use href="#icon-pause" /></svg></span>
          </button>
          <div class="product-gallery-modal__controls-row">
          <button class="product-gallery-modal__button product-gallery-modal__button--prev" aria-label="Previous image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button class="product-gallery-modal__button product-gallery-modal__button--close" aria-label="Close zoom">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <button class="product-gallery-modal__button product-gallery-modal__button--next" aria-label="Next image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          </div>
        </div>
      </div>
    `;
    
    // Event handlers
    const prevButton = modal.querySelector('.product-gallery-modal__button--prev');
    const nextButton = modal.querySelector('.product-gallery-modal__button--next');
    const closeButton = modal.querySelector('.product-gallery-modal__button--close');
    const image = modal.querySelector('.product-gallery-modal__image');
    const imageContainer = modal.querySelector('.product-gallery-modal__image-container');
    
    prevButton.addEventListener('click', () => this.prevMedia());
    nextButton.addEventListener('click', () => this.nextMedia());
    closeButton.addEventListener('click', () => this.closeZoomModal());
    
    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeZoomModal();
      }
    });
    
    // ESC key
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeZoomModal();
      } else if (e.key === 'ArrowLeft') {
        this.prevMedia();
      } else if (e.key === 'ArrowRight') {
        this.nextMedia();
      }
    });
    
    // Pan when zoomed (must be before click handler)
    this.initPan(imageContainer, image);
    
    // Pinch to zoom (mobile)
    this.initPinchZoom(imageContainer, image);
    
    // Hide tap hint after first interaction (tap or drag)
    const dismissTapHint = () => {
      const tapHint = modal.querySelector('.product-gallery-modal__tap-hint');
      if (tapHint) tapHint.classList.add('is-dismissed');
    };
    imageContainer.addEventListener('touchstart', dismissTapHint);
    imageContainer.addEventListener('mousedown', dismissTapHint);
    image.addEventListener('click', () => {
      if (this.isVideoInModal) return;
      if (!this.wasDragging) {
        this.toggleZoom();
      }
      this.wasDragging = false;
      dismissTapHint();
    });
    
    return modal;
  }
  
  updateModalVideo(mediaIndex, sourceElement, image, videoEl, imageContainer) {
    const modal = document.getElementById('product-gallery-modal');
    if (!modal || !videoEl) return;
    
    // Always switch to video mode first so we don't show a stale image
    image.style.display = 'none';
    videoEl.style.display = 'block';
    videoEl.innerHTML = '';
    
    // Explicitly hide spinner (may have inline styles from previous image load)
    const spinner = modal.querySelector('.product-gallery-modal__loading-spinner');
    if (spinner) {
      spinner.classList.remove('active');
      spinner.style.display = '';
    }
    
    // Defer video setup until modal is laid out (fixes initial open - modal may have zero size before first paint)
    const setupVideo = () => {
    let videoSources = [];
    const clickedVideo = sourceElement?.querySelector?.('video') ?? (sourceElement?.tagName === 'VIDEO' ? sourceElement : null);
    // Prefer mediaData for video sources - DOM sources may be empty with preload:none
    if (this.mediaData?.media?.[mediaIndex]?.video?.sources?.length) {
      const raw = this.mediaData.media[mediaIndex].video.sources.map((s) => {
        let src = s.src || s.url;
        if (src && src.startsWith('//')) {
          src = (typeof location !== 'undefined' && location.protocol) ? location.protocol + src : 'https:' + src;
        }
        return { src, mime_type: s.mime_type || s.type || 'video/mp4' };
      }).filter((s) => s.src);
      // Prefer mp4 over m3u8 for broader compatibility; use all if none match
      const mp4 = raw.filter((s) => (s.mime_type || '').includes('mp4') || (s.src || '').includes('.mp4'));
      videoSources = (mp4.length ? mp4 : raw);
    }
    if (videoSources.length === 0 && clickedVideo) {
      clickedVideo.querySelectorAll?.('source').forEach((s) => {
        if (s.src) videoSources.push({ src: s.src, mime_type: s.type || 'video/mp4' });
      });
      if (videoSources.length === 0 && clickedVideo.src) {
        videoSources.push({ src: clickedVideo.src, mime_type: clickedVideo.type || 'video/mp4' });
      }
    }
    if (videoSources.length === 0) {
      const slides = this.container.querySelectorAll('[data-slide-index]');
      const slide = slides[mediaIndex];
      const domVideo = slide?.querySelector('video');
      if (domVideo) {
        domVideo.querySelectorAll('source').forEach((s) => {
          if (s.src) videoSources.push({ src: s.src, mime_type: s.type || 'video/mp4' });
        });
        if (videoSources.length === 0 && domVideo.src) {
          videoSources.push({ src: domVideo.src, mime_type: domVideo.type || 'video/mp4' });
        }
      }
    }
    if (videoSources.length === 0) {
      const desktopItem = this.container.querySelector(`.product-gallery__item[data-media-index="${mediaIndex}"]`);
      const desktopVideo = desktopItem?.querySelector('video');
      if (desktopVideo) {
        desktopVideo.querySelectorAll('source').forEach((s) => {
          if (s.src) videoSources.push({ src: s.src, mime_type: s.type || 'video/mp4' });
        });
        if (videoSources.length === 0 && desktopVideo.src) {
          videoSources.push({ src: desktopVideo.src, mime_type: desktopVideo.type || 'video/mp4' });
        }
      }
    }
    
    if (videoSources.length === 0) return;
    
    videoSources.forEach((src) => {
      const source = document.createElement('source');
      source.src = src.src;
      source.type = src.mime_type || 'video/mp4';
      videoEl.appendChild(source);
    });
    videoEl.muted = true;
    videoEl.loop = true;
    videoEl.playsInline = true;
    
    const playPauseBtn = modal.querySelector('.product-gallery-modal__video-play-pause');
    if (playPauseBtn) {
      playPauseBtn.style.display = 'flex';
      playPauseBtn.classList.remove('is-playing');
      playPauseBtn.setAttribute('aria-label', 'Play video');
      const syncVideoBtn = () => {
        playPauseBtn.classList.toggle('is-playing', !videoEl.paused);
        playPauseBtn.setAttribute('aria-label', videoEl.paused ? 'Play video' : 'Pause video');
      };
      videoEl.onplay = syncVideoBtn;
      videoEl.onpause = syncVideoBtn;
      playPauseBtn.onclick = (e) => {
        e.stopPropagation();
        if (videoEl.paused) {
          videoEl.play().catch(() => {});
        } else {
          videoEl.pause();
        }
      };
    }
    
    videoEl.load();
    const playWhenReady = () => {
      videoEl.play().catch(() => {});
    };
    if (videoEl.readyState >= 2) {
      playWhenReady();
    } else {
      videoEl.addEventListener('loadeddata', playWhenReady, { once: true });
    }
    
    // Ensure video is visible - skip animateZoomIn for video (causes display issues on initial open;
    // video needs to load before dimensions are known, and navigation works because it skips animation)
    videoEl.style.opacity = '1';
    videoEl.style.transform = '';
    videoEl.style.transition = '';
    };
    
    // Only defer on initial open (sourceElement present) - modal may need layout; when navigating, modal is already laid out
    if (sourceElement) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!modal.isConnected || !videoEl.isConnected) return;
          setupVideo();
        });
      });
    } else {
      setupVideo();
    }
  }
  
  updateModalImage(mediaIndex, onLoadCallback = null) {
    
    const modal = document.getElementById('product-gallery-modal');
    if (!modal) return;
    
    const image = modal.querySelector('.product-gallery-modal__image');
    const videoEl = modal.querySelector('.product-gallery-modal__video');
    const playPauseBtn = modal.querySelector('.product-gallery-modal__video-play-pause');
    const loadingSpinner = modal.querySelector('.product-gallery-modal__loading-spinner');
    if (!image) return;
    
    image.style.display = '';
    if (videoEl) videoEl.style.display = 'none';
    if (playPauseBtn) playPauseBtn.style.display = 'none';
    if (videoEl) {
      videoEl.pause();
      videoEl.innerHTML = '';
    }
    
    // Function to hide spinner and show image
    const hideSpinnerAndShowImage = () => {
      // Force hide spinner
      if (loadingSpinner) {
        loadingSpinner.classList.remove('active');
        // Also set display none as backup
        loadingSpinner.style.display = 'none';
      }
      // Show tap hint only after image is loaded
      const tapHint = modal.querySelector('.product-gallery-modal__tap-hint');
      if (tapHint) tapHint.classList.remove('is-loading');
      if (onLoadCallback) {
        onLoadCallback();
      } else {
        image.style.opacity = '1';
      }
    };
    
    // Hide image while loading new source to prevent flash
    image.style.opacity = '0';
    // Hide tap hint until image is loaded
    const tapHint = modal.querySelector('.product-gallery-modal__tap-hint');
    if (tapHint) tapHint.classList.add('is-loading');
    
    // Show loading spinner - ensure it's centered and stable
    if (loadingSpinner) {
      // Remove any inline styles that might interfere with CSS positioning
      loadingSpinner.style.top = '';
      loadingSpinner.style.left = '';
      loadingSpinner.style.right = '';
      loadingSpinner.style.bottom = '';
      loadingSpinner.style.transform = '';
      loadingSpinner.style.margin = '';
      loadingSpinner.style.padding = '';
      loadingSpinner.style.width = '';
      loadingSpinner.style.height = '';
      
      // Show spinner immediately but ensure it's properly positioned
      // Use double RAF to ensure layout is stable and container is ready
      const imageContainer = modal.querySelector('.product-gallery-modal__image-container');
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (loadingSpinner && modal.contains(loadingSpinner) && imageContainer) {
            // Ensure container has dimensions before showing spinner
            const containerRect = imageContainer.getBoundingClientRect();
            
            if (containerRect.width > 0 && containerRect.height > 0) {
              // Check if image is already loaded before showing spinner
              if (image.complete && image.naturalWidth > 0) {
                return; // Don't show spinner if image is already loaded
              }
              // Explicitly set position and inset to ensure CSS is applied
              // CSS position: absolute isn't being applied (computed as static), so set it explicitly
              loadingSpinner.style.position = 'absolute';
              loadingSpinner.style.top = '0';
              loadingSpinner.style.left = '0';
              loadingSpinner.style.right = '0';
              loadingSpinner.style.bottom = '0';
              loadingSpinner.style.display = 'flex';
              loadingSpinner.style.alignItems = 'center';
              loadingSpinner.style.justifyContent = 'center';
              loadingSpinner.classList.add('active');
            } else {
              // If container not ready, try one more time
              setTimeout(() => {
                if (loadingSpinner && modal.contains(loadingSpinner) && imageContainer) {
                  if (image.complete && image.naturalWidth > 0) {
                    return; // Don't show spinner if image is already loaded
                  }
                  const fallbackRect = imageContainer.getBoundingClientRect();
                  if (fallbackRect.width > 0 && fallbackRect.height > 0) {
                    loadingSpinner.style.position = 'absolute';
                    loadingSpinner.style.top = '0';
                    loadingSpinner.style.left = '0';
                    loadingSpinner.style.right = '0';
                    loadingSpinner.style.bottom = '0';
                    loadingSpinner.style.display = 'flex';
                    loadingSpinner.style.alignItems = 'center';
                    loadingSpinner.style.justifyContent = 'center';
                    loadingSpinner.classList.add('active');
                  }
                }
              }, 50);
            }
          }
        });
      });
    }
    
    // Determine new image source - mediaIndex is the original media index
    let newSrc = '';
    
    if (this.mediaData && this.mediaData.media && this.mediaData.media[mediaIndex] && this.mediaData.media[mediaIndex].media_type === 'image') {
      const media = this.mediaData.media[mediaIndex];
      newSrc = media.sources.desktop;
      image.alt = media.alt || 'Product image';
    }
    
    if (!newSrc && this.mediaData && this.mediaData.media[mediaIndex]) {
      const media = this.mediaData.media[mediaIndex];
      if (media.media_type === 'image') {
        newSrc = media.sources.desktop;
        image.alt = media.alt || 'Product image';
      }
      
      // Ensure no width parameters in mediaData source (safety check)
      if (newSrc && (newSrc.includes('&width=') || newSrc.includes('?width='))) {
        newSrc = newSrc.replace(/[&?]width=[0-9]+/g, '');
        newSrc = newSrc.replace(/[&?]height=[0-9]+/g, '');
        newSrc = newSrc.replace(/&+/g, '&');
        newSrc = newSrc.replace(/\?&/g, '?');
        newSrc = newSrc.replace(/[&?]$/, '');
        if (newSrc.includes('&') && !newSrc.includes('?')) {
          const parts = newSrc.split('&');
          newSrc = parts[0] + '?' + parts.slice(1).join('&');
        }
      }
    } else {
      // Fallback to image from DOM by media index
      const clickedImage = this.container.querySelector(`[data-gallery-image][data-media-index="${mediaIndex}"]`) || this.container.querySelector(`[data-media-index="${mediaIndex}"] [data-gallery-image]`);
      if (clickedImage) {
        const img = clickedImage.querySelector('img');
        if (img) {
          // First, try to get the full resolution from data attribute
          if (img.dataset.fullImage) {
            newSrc = img.dataset.fullImage;
          }
          // Otherwise, try to get the highest resolution from srcset
          else if (img.srcset) {
            const srcsetEntries = img.srcset.split(',').map(entry => {
              const parts = entry.trim().split(/\s+/);
              return {
                url: parts[0],
                width: parts[1] ? parseInt(parts[1]) : 0
              };
            });
            // Sort by width descending and get the largest
            srcsetEntries.sort((a, b) => b.width - a.width);
            if (srcsetEntries.length > 0 && srcsetEntries[0].width > 0) {
              newSrc = srcsetEntries[0].url;
            } else {
              newSrc = img.src;
            }
          } else {
            newSrc = img.src;
          }
          // Convert to full resolution - handle file images with query parameters
          if (newSrc) {
            // For file images (using query parameters like &width=750 or ?width=750)
            if (newSrc.includes('&width=') || newSrc.includes('?width=')) {
              // Remove width and height parameters using regex (works for both absolute and relative URLs)
              newSrc = newSrc.replace(/[&?]width=[0-9]+/g, '');
              newSrc = newSrc.replace(/[&?]height=[0-9]+/g, '');
              // Clean up: remove trailing &, fix ?& to ?, remove trailing ?
              newSrc = newSrc.replace(/&+/g, '&');
              newSrc = newSrc.replace(/\?&/g, '?');
              newSrc = newSrc.replace(/[&?]$/, '');
              // If we removed the ? but there are still params, add it back
              if (newSrc.includes('&') && !newSrc.includes('?')) {
                const parts = newSrc.split('&');
                newSrc = parts[0] + '?' + parts.slice(1).join('&');
              }
            }
            // For product media images (using path-based sizing like _750x or /750x/)
            else if (newSrc.includes('cdn.shopify.com') && !newSrc.includes('_master') && !newSrc.includes('/master/')) {
              // Replace size parameter with master
              newSrc = newSrc.replace(/_[0-9]+x[0-9]*(\.[a-z]+)?(\?|$)/, '_master$1$2');
              newSrc = newSrc.replace(/\/([0-9]+x[0-9]*)\//, '/master/');
            }
          }
          image.alt = img.alt;
        }
      }
    }
    
    // Check if this is the same image (already loaded)
    const currentSrc = image.src;
    
    if (newSrc && currentSrc && newSrc === currentSrc && image.complete && image.naturalWidth > 0) {
      requestAnimationFrame(() => {
        hideSpinnerAndShowImage();
      });
      this.currentIndex = mediaIndex;
      this.currentMediaIndex = mediaIndex;
      this.zoomLevel = 1;
      this.updateZoomClass();
      return;
    }
    
    // Set new image source
    if (newSrc) {
      image.src = newSrc;
    }
    
    this.currentIndex = mediaIndex;
    this.currentMediaIndex = mediaIndex;
    this.zoomLevel = 1;
    this.updateZoomClass();
    
    // Function to check if image is loaded and hide spinner
    let loadHandled = false;
    const checkAndHideSpinner = () => {
      if (loadHandled) return false;
      
      // Check if image is loaded
      const isLoaded = image.complete && image.naturalWidth > 0;
      
      if (isLoaded) {
        loadHandled = true;
        hideSpinnerAndShowImage();
        return true;
      }
      return false;
    };
    
    // Check immediately (for cached images)
    if (checkAndHideSpinner()) {
      return;
    }
    
    // Set up load event handlers
    const handleLoad = () => {
      if (!loadHandled) {
        loadHandled = true;
        clearTimeout(timeoutId);
        hideSpinnerAndShowImage();
      }
    };
    
    const handleError = () => {
      if (!loadHandled) {
        loadHandled = true;
        clearTimeout(timeoutId);
        hideSpinnerAndShowImage();
      }
    };
    
    // Fallback timeout to ensure spinner always disappears (2 seconds max)
    const timeoutId = setTimeout(() => {
      if (!loadHandled) {
        loadHandled = true;
        hideSpinnerAndShowImage();
      }
    }, 2000);
    
    // Add event listeners
    image.addEventListener('load', handleLoad, { once: true });
    image.addEventListener('error', handleError, { once: true });
    
    // Also check periodically in case load event doesn't fire (for cached images)
    let checkCount = 0;
    const maxChecks = 10; // Check for ~500ms
    const checkInterval = setInterval(() => {
      checkCount++;
      if (checkAndHideSpinner() || checkCount >= maxChecks) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
      }
    }, 50);
  }
  
  prevMedia() {
    const totalMedia = this.mediaData?.media?.length ?? this.container.querySelectorAll('[data-slide-index]').length;
    const newIndex = (this.currentMediaIndex - 1 + totalMedia) % totalMedia;
    this.goToModalMedia(newIndex);
  }
  
  nextMedia() {
    const totalMedia = this.mediaData?.media?.length ?? this.container.querySelectorAll('[data-slide-index]').length;
    const newIndex = (this.currentMediaIndex + 1) % totalMedia;
    this.goToModalMedia(newIndex);
  }
  
  goToModalMedia(mediaIndex) {
    const modal = document.getElementById('product-gallery-modal');
    if (!modal) return;
    const media = this.mediaData?.media?.[mediaIndex];
    if (!media) return;
    
    this.currentMediaIndex = mediaIndex;
    this.currentIndex = mediaIndex;
    
    if (media.media_type === 'video') {
      this.isVideoInModal = true;
      modal.classList.add('product-gallery-modal--video');
      const image = modal.querySelector('.product-gallery-modal__image');
      const videoEl = modal.querySelector('.product-gallery-modal__video');
      const imageContainer = modal.querySelector('.product-gallery-modal__image-container');
      this.updateModalVideo(mediaIndex, null, image, videoEl, imageContainer);
    } else {
      this.isVideoInModal = false;
      modal.classList.remove('product-gallery-modal--video');
      this.updateModalImage(mediaIndex);
    }
  }
  
  toggleZoom() {
    if (this.zoomLevel === 1) {
      this.zoomLevel = 1.5;
    } else if (this.zoomLevel === 1.5) {
      this.zoomLevel = 3;
    } else {
      this.zoomLevel = 1;
    }
    this.updateZoomClass();
  }
  
  updateZoomClass() {
    const modal = document.getElementById('product-gallery-modal');
    if (!modal) return;
    
    const image = modal.querySelector('.product-gallery-modal__image');
    if (!image) return;
    
    // Reset pan position when zoom changes
    if (this.resetPan) {
      this.resetPan();
    }
    
    image.className = 'product-gallery-modal__image';
    if (this.zoomLevel === 1) {
      image.classList.add('zoom-1x');
      image.style.transform = 'scale(1)';
    } else if (this.zoomLevel === 1.5) {
      image.classList.add('zoom-1_5x');
      image.style.transform = 'scale(1.5)';
    } else if (this.zoomLevel === 3) {
      image.classList.add('zoom-3x');
      image.style.transform = 'scale(3)';
    }
    
    this.isZoomed = this.zoomLevel > 1;
  }
  
  initPinchZoom(container, image) {
    let touches = [];
    
    container.addEventListener('touchstart', (e) => {
      if (this.isVideoInModal) return;
      touches = Array.from(e.touches);
      if (touches.length === 2) {
        this.pinchStartDistance = this.getDistance(touches[0], touches[1]);
        this.pinchStartZoom = this.zoomLevel;
      }
    });
    
    container.addEventListener('touchmove', (e) => {
      if (this.isVideoInModal) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / this.pinchStartDistance;
        const newZoom = this.pinchStartZoom * scale;
        
        // Clamp zoom between 1 and 3
        this.zoomLevel = Math.max(1, Math.min(3, newZoom));
        
        // Apply custom transform
        image.style.transform = `scale(${this.zoomLevel})`;
        image.className = 'product-gallery-modal__image';
        this.isZoomed = this.zoomLevel > 1;
      }
    });
    
    container.addEventListener('touchend', () => {
      if (touches.length === 2) {
        // Snap to nearest zoom level
        if (this.zoomLevel < 1.25) {
          this.zoomLevel = 1;
        } else if (this.zoomLevel < 2.25) {
          this.zoomLevel = 1.5;
        } else {
          this.zoomLevel = 3;
        }
        this.updateZoomClass();
        image.style.transform = '';
      }
      touches = [];
    });
  }
  
  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  initPan(container, image) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let hasMoved = false;
    const DRAG_THRESHOLD = 5; // pixels
    let mouseStartX = 0;
    let mouseStartY = 0;
    
    // Inertia/momentum variables
    let velocityX = 0;
    let velocityY = 0;
    let lastX = 0;
    let lastY = 0;
    let lastTime = 0;
    let animationId = null;
    const FRICTION = 0.92; // Deceleration factor (0-1, higher = more slide)
    const MIN_VELOCITY = 0.5; // Stop animation below this velocity
    
    // Cached rects for clampPan (invalidated when zoom changes)
    let cachedZoomLevel = null;
    let cachedMaxPanX = 0;
    let cachedMaxPanY = 0;
    
    // Calculate pan bounds based on zoom level and image/container dimensions
    const clampPan = (x, y) => {
      if (cachedZoomLevel !== this.zoomLevel) {
        const containerRect = container.getBoundingClientRect();
        const imageRect = image.getBoundingClientRect();
        const baseWidth = imageRect.width / this.zoomLevel;
        const baseHeight = imageRect.height / this.zoomLevel;
        const zoomedWidth = baseWidth * this.zoomLevel;
        const zoomedHeight = baseHeight * this.zoomLevel;
        cachedMaxPanX = Math.max(0, (zoomedWidth - containerRect.width) / 2);
        cachedMaxPanY = Math.max(0, (zoomedHeight - containerRect.height) / 2);
        cachedZoomLevel = this.zoomLevel;
      }
      
      const clampedX = Math.max(-cachedMaxPanX, Math.min(cachedMaxPanX, x));
      const clampedY = Math.max(-cachedMaxPanY, Math.min(cachedMaxPanY, y));
      
      return { x: clampedX, y: clampedY };
    };
    
    const applyTransform = () => {
      const clamped = clampPan(currentX, currentY);
      currentX = clamped.x;
      currentY = clamped.y;
      image.style.transform = `translate(${currentX}px, ${currentY}px) scale(${this.zoomLevel})`;
    };
    
    // Stop any ongoing inertia animation
    const stopInertia = () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      velocityX = 0;
      velocityY = 0;
    };
    
    // Inertia animation loop
    const animateInertia = () => {
      // Apply friction to slow down
      velocityX *= FRICTION;
      velocityY *= FRICTION;
      
      // Update position
      currentX += velocityX;
      currentY += velocityY;
      
      // Apply bounds and transform
      const clamped = clampPan(currentX, currentY);
      
      // Check if we hit a boundary - reduce velocity in that direction
      if (clamped.x !== currentX) velocityX = 0;
      if (clamped.y !== currentY) velocityY = 0;
      
      currentX = clamped.x;
      currentY = clamped.y;
      image.style.transform = `translate(${currentX}px, ${currentY}px) scale(${this.zoomLevel})`;
      
      // Continue animation if velocity is significant
      if (Math.abs(velocityX) > MIN_VELOCITY || Math.abs(velocityY) > MIN_VELOCITY) {
        animationId = requestAnimationFrame(animateInertia);
      } else {
        animationId = null;
        image.classList.remove('is-dragging');
      }
    };
    
    // Reset pan position when zoom changes
    this.resetPan = () => {
      stopInertia();
      currentX = 0;
      currentY = 0;
      cachedZoomLevel = null; // Invalidate clampPan cache
    };
    
    container.addEventListener('mousedown', (e) => {
      if (this.isVideoInModal) return;
      if (this.zoomLevel >= 1) {
        stopInertia(); // Stop any ongoing inertia
        isPanning = true;
        hasMoved = false;
        mouseStartX = e.clientX;
        mouseStartY = e.clientY;
        startX = e.clientX - currentX;
        startY = e.clientY - currentY;
        lastX = e.clientX;
        lastY = e.clientY;
        lastTime = performance.now();
        container.style.cursor = 'grabbing';
        image.classList.add('is-dragging'); // Disable transition for instant feedback
        e.preventDefault(); // Prevent text selection
      }
    });
    
    container.addEventListener('mousemove', (e) => {
      if (isPanning && this.zoomLevel >= 1) {
        e.preventDefault();
        
        // Check if we've moved past the drag threshold
        const deltaX = Math.abs(e.clientX - mouseStartX);
        const deltaY = Math.abs(e.clientY - mouseStartY);
        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          hasMoved = true;
          this.wasDragging = true;
        }
        
        if (!prefersReducedMotion) {
          const now = performance.now();
          const dt = now - lastTime;
          if (dt > 0) {
            velocityX = (e.clientX - lastX) / dt * 16; // Normalize to ~60fps
            velocityY = (e.clientY - lastY) / dt * 16;
          }
          lastX = e.clientX;
          lastY = e.clientY;
          lastTime = now;
        }
        
        currentX = e.clientX - startX;
        currentY = e.clientY - startY;
        applyTransform();
      }
    });
    
    container.addEventListener('mouseup', () => {
      if (isPanning && this.zoomLevel >= 1) {
        isPanning = false;
        container.style.cursor = this.isZoomed ? 'grab' : 'default';
        
        if (prefersReducedMotion) {
          image.classList.remove('is-dragging');
        } else if (Math.abs(velocityX) > MIN_VELOCITY || Math.abs(velocityY) > MIN_VELOCITY) {
          animationId = requestAnimationFrame(animateInertia);
        } else {
          image.classList.remove('is-dragging');
        }
      }
    });
    
    container.addEventListener('mouseleave', () => {
      if (isPanning && this.zoomLevel >= 1) {
        isPanning = false;
        container.style.cursor = this.isZoomed ? 'grab' : 'default';
        
        if (prefersReducedMotion) {
          image.classList.remove('is-dragging');
        } else if (Math.abs(velocityX) > MIN_VELOCITY || Math.abs(velocityY) > MIN_VELOCITY) {
          animationId = requestAnimationFrame(animateInertia);
        } else {
          image.classList.remove('is-dragging');
        }
      }
    });
    
    // Touch pan (at 1x and when zoomed)
    let touchStartX = 0;
    let touchStartY = 0;
    let isTouchPanning = false;
    let touchLastX = 0;
    let touchLastY = 0;
    let touchLastTime = 0;
    container.addEventListener('touchstart', (e) => {
      if (this.isVideoInModal) return;
      if (e.touches.length === 1) {
        touchLastX = e.touches[0].clientX;
        touchLastY = e.touches[0].clientY;
        touchLastTime = performance.now();
        if (this.zoomLevel >= 1) {
          stopInertia();
          isTouchPanning = true;
          touchStartX = e.touches[0].clientX - currentX;
          touchStartY = e.touches[0].clientY - currentY;
          touchLastX = e.touches[0].clientX;
          touchLastY = e.touches[0].clientY;
          image.classList.add('is-dragging');
        }
      }
    });
    
    container.addEventListener('touchmove', (e) => {
      if (this.zoomLevel >= 1 && e.touches.length === 1) {
        e.preventDefault();
        
        if (!prefersReducedMotion) {
          const now = performance.now();
          const dt = now - touchLastTime;
          if (dt > 0) {
            velocityX = (e.touches[0].clientX - touchLastX) / dt * 16;
            velocityY = (e.touches[0].clientY - touchLastY) / dt * 16;
          }
          touchLastX = e.touches[0].clientX;
          touchLastY = e.touches[0].clientY;
          touchLastTime = now;
        }
        
        currentX = e.touches[0].clientX - touchStartX;
        currentY = e.touches[0].clientY - touchStartY;
        applyTransform();
      }
    });
    
    container.addEventListener('touchend', (e) => {
      if (isTouchPanning) {
        isTouchPanning = false;
        
        if (prefersReducedMotion) {
          image.classList.remove('is-dragging');
        } else if (Math.abs(velocityX) > MIN_VELOCITY || Math.abs(velocityY) > MIN_VELOCITY) {
          animationId = requestAnimationFrame(animateInertia);
        } else {
          image.classList.remove('is-dragging');
        }
      }
    });
  }
  
  closeZoomModal() {
    const modal = document.getElementById('product-gallery-modal');
    if (modal) {
      modal.classList.remove('active');
      modal.classList.remove('product-gallery-modal--single-image');
      modal.classList.remove('product-gallery-modal--video');
      document.body.style.overflow = '';
      
      this.zoomLevel = 1;
      this.isZoomed = false;
      this.isVideoInModal = false;
      if (this.resetPan) {
        this.resetPan();
      }
      
      const image = modal.querySelector('.product-gallery-modal__image');
      const videoEl = modal.querySelector('.product-gallery-modal__video');
      const playPauseBtn = modal.querySelector('.product-gallery-modal__video-play-pause');
      const loadingSpinner = modal.querySelector('.product-gallery-modal__loading-spinner');
      if (image) {
        image.style.transform = 'scale(1)';
        image.className = 'product-gallery-modal__image zoom-1x';
        image.style.display = '';
      }
      if (videoEl) {
        videoEl.pause();
        videoEl.innerHTML = '';
        videoEl.style.display = 'none';
      }
      if (playPauseBtn) {
        playPauseBtn.style.display = 'none';
      }
      if (loadingSpinner) {
        loadingSpinner.classList.remove('active');
      }
      const tapHint = modal.querySelector('.product-gallery-modal__tap-hint');
      if (tapHint) {
        tapHint.classList.remove('is-dismissed');
        tapHint.classList.add('is-loading');
      }
    }
  }
  
  initVideos() {
    // Desktop videos: Ensure autoplay on mount
    const desktopVideos = this.container.querySelectorAll('[data-video-desktop] video');
    desktopVideos.forEach(video => {
      // Ensure video is muted for autoplay compliance
      video.muted = true;
      // Try to play
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Desktop video autoplay failed:', error);
        });
      }
    });
    
    // Mobile videos: IntersectionObserver for scroll-based play/pause
    if (window.innerWidth < 768) {
      this.initMobileVideoObserver();
    }
  }
  
  initMobileVideoObserver() {
    const mobileVideos = this.container.querySelectorAll('[data-video-mobile] video');
    if (mobileVideos.length === 0) return;
    
    // Create IntersectionObserver for mobile videos
    // Use rootMargin to preload when approaching viewport
    const observerOptions = {
      root: null, // viewport
      rootMargin: '50% 0%', // Start loading when 50% away from viewport
      threshold: [0, 0.1, 0.5, 1] // Multiple thresholds for smoother behavior
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // Video is in viewport - play it
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              // Playback started successfully
            }).catch(error => {
              console.log('Mobile video play failed:', error);
              // Try again with explicit muted setting
              video.muted = true;
              video.play().catch(e => {
                console.log('Mobile video play retry failed:', e);
              });
            });
          }
        } else {
          // Video is out of viewport - pause it
          video.pause();
        }
      });
    }, observerOptions);
    
    // Observe all mobile videos
    mobileVideos.forEach(video => {
      // Ensure video is muted for autoplay
      video.muted = true;
      // Start observing
      observer.observe(video);
    });
    
    // Store observer for potential cleanup
    this.mobileVideoObserver = observer;
  }
  
  destroy() {
    // Cleanup if needed
    this.closeZoomModal();
    
    // Disconnect video observer if it exists
    if (this.mobileVideoObserver) {
      this.mobileVideoObserver.disconnect();
      this.mobileVideoObserver = null;
    }
  }
}

// Initialize galleries on page load
document.addEventListener('DOMContentLoaded', () => {
  const galleryContainers = document.querySelectorAll('[data-gallery-container]');
  galleryContainers.forEach(container => {
    new ProductGallery(container);
  });
});

// Export for re-initialization
window.ProductGallery = ProductGallery;

